// src/services/api.ts — Axios client con JWT interceptors (POW-02)
import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { decidirAnte401 } from '@/utils/authGuards'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
})

// ── Interceptor de request: adjunta JWT ──────────────────────────────
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('pow_access_token')
  if (token) config.headers['Authorization'] = `Bearer ${token}`
  return config
})

// ── Interceptor de respuesta: renueva el token ante 401 / cierra sesión si es inválida ──
let isRefreshing = false
let failedQueue: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)))
  failedQueue = []
}

/** Limpia tokens y resetea el estado de autenticación. */
async function resetSession() {
  localStorage.removeItem('pow_access_token')
  localStorage.removeItem('pow_refresh_token')
  localStorage.removeItem('pow_otp_session')
  localStorage.removeItem('pow-auth')
  // El Bearer cacheado en defaults sobrevive al borrado de localStorage y se
  // seguiría enviando tras el logout (BUG-04): limpiarlo explícitamente.
  delete api.defaults.headers.common['Authorization']
  try {
    const { useAuthStore } = await import('@/store/authStore')
    useAuthStore.getState().logout()
  } catch {
    /* noop */
  }
}

function _redirectToLogin() {
  if (window.location.pathname === '/' || window.location.pathname === '/login') return

  const returnTo = window.location.pathname + window.location.search
  window.location.href = `/?returnTo=${encodeURIComponent(returnTo)}`
}

/** true cuando el backend responde 403 "Not authenticated" (la petición no llevó credenciales). */
function isAuthMissing(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { detail?: unknown } } }
  const detail = e.response?.data?.detail
  return e.response?.status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('not authenticated')
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Sesión sin token: cerrar sesión y volver al login (evita "Not authenticated" en pantalla).
    if (isAuthMissing(err)) {
      await resetSession()
      _redirectToLogin()
      return Promise.reject(err)
    }

    // Si no es 401, rechaza directamente sin intentar renovar
    if (err.response?.status !== 401) {
      return Promise.reject(err)
    }

    // Decisión pura y testeable (BUG-17): auth anónima → ignorar; sin refresh o
    // reintento fallido → cerrar sesión; refresh en curso → reencolar; resto → refrescar.
    const accion = decidirAnte401({
      url: original?.url,
      hayRefresh: Boolean(localStorage.getItem('pow_refresh_token')),
      refrescando: isRefreshing,
      yaReintentado: Boolean(original._retry),
    })

    if (accion === 'ignorar') {
      return Promise.reject(err)
    }
    if (accion === 'cerrar-sesion') {
      await resetSession()
      _redirectToLogin()
      return Promise.reject(err)
    }
    if (accion === 'reintentar') {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token) => {
        // Marcar el reintento: si vuelve a dar 401 se cierra sesión en vez de
        // reiniciar otro refresh (BUG-03, evita el ciclo contra /token/renovar).
        original._retry = true
        original.headers['Authorization'] = `Bearer ${token}`
        return api(original)
      })
    }

    const refresh = localStorage.getItem('pow_refresh_token')
    if (!refresh) {
      // Sin refresh token → ir a login sin dejar isRefreshing en true (LOGIN-02)
      await resetSession()
      _redirectToLogin()
      return Promise.reject(err)
    }

    original._retry = true
    isRefreshing = true

    try {
      const { data } = await axios.post(`${BASE_URL}/api/auth/token/renovar`, {
        refresh_token: refresh,
      })
      const newToken: string = data.access_token
      localStorage.setItem('pow_access_token', newToken)
      if (data.refresh_token) {
        localStorage.setItem('pow_refresh_token', data.refresh_token)
      }

      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
      original.headers['Authorization'] = `Bearer ${newToken}`
      processQueue(null, newToken)
      return api(original)
    } catch (refreshErr) {
      processQueue(refreshErr, null)
      await resetSession()
      _redirectToLogin()
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

export default api
